import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { 
    Download, FileText, Printer, BarChart2, PieChart, TrendingUp, 
    DollarSign, Calendar, Users, List, Grid, Search, AlertTriangle, 
    CheckCircle, Clock, Fuel, MapPin, Activity, Hash, BadgeDollarSign
} from 'lucide-react';
import useApi from '../hooks/useApi';

// Helper reforçado para evitar problemas de fuso horário
const formatDateUTC = (dateString) => {
    if (!dateString) return '-';
    const dateOnly = dateString.split('T')[0]; 
    return new Date(dateOnly).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
};

// --- COMPONENTE DE FILTRO DE USUÁRIO (Reutilizável para Admin) ---
const UserFilterSelect = ({ users, selectedUserId, setSelectedUserId, currentUser }) => {
    // Só renderiza se for Super Usuário ou Admin
    const isSuperOrAdmin = currentUser?.is_super_user || currentUser?.permissions?.manageUsers;
    
    if (!isSuperOrAdmin) return null;

    return (
        <div className="min-w-[200px]">
            <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Filtrar por Usuário</label>
            <select 
                value={selectedUserId} 
                onChange={e => setSelectedUserId(e.target.value)} 
                className="w-full p-2 border rounded border-gray-300 focus:border-blue-500 outline-none text-sm"
            >
                <option value="todos">Todos os Usuários (Unificado)</option>
                {users.map(u => (
                    <option key={u.id} value={u.id}>{u.username}</option>
                ))}
            </select>
        </div>
    );
};

// --- A. LIVRO CAIXA ---
const CashBookReport = ({ formatCurrency, selectedCurrency, users, currentUser }) => {
    const [month, setMonth] = useState(new Date().getMonth() + 1);
    const [year, setYear] = useState(new Date().getFullYear());
    const [selectedUserId, setSelectedUserId] = useState('todos');
    const [data, setData] = useState(null);
    const { apiFetch } = useApi();

    const fetchReport = async (e) => {
        if(e) e.preventDefault();
        try {
            const userQuery = selectedUserId !== 'todos' ? `&userId=${selectedUserId}` : '';
            const res = await apiFetch(`/reports/cash-flow?month=${month}&year=${year}${userQuery}`);
            setData(res);
        } catch (error) { console.error(error); }
    };

    return (
        <div>
            <form onSubmit={fetchReport} className="flex flex-wrap gap-4 mb-6 items-end print:hidden bg-gray-50 p-4 rounded border border-gray-200">
                <UserFilterSelect users={users} selectedUserId={selectedUserId} setSelectedUserId={setSelectedUserId} currentUser={currentUser} />
                
                <div>
                    <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Mês</label>
                    <input type="number" min="1" max="12" value={month} onChange={e => setMonth(e.target.value)} className="w-20 p-2 border rounded text-sm" />
                </div>
                <div>
                    <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Ano</label>
                    <input type="number" value={year} onChange={e => setYear(e.target.value)} className="w-24 p-2 border rounded text-sm" />
                </div>
                <button type="submit" className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-bold shadow-sm transition-colors text-sm h-[38px]">
                    Gerar Livro Caixa
                </button>
            </form>

            {data && (
                <>
                    <div className="hidden print:block mb-4 text-center">
                        <h2 className="text-xl font-bold uppercase tracking-wider">Livro Caixa - {month.toString().padStart(2, '0')}/{year}</h2>
                        {selectedUserId !== 'todos' && <p className="text-sm text-gray-600">Filtrado por: {users.find(u => String(u.id) === String(selectedUserId))?.username}</p>}
                    </div>

                    <div className="bg-white rounded shadow overflow-hidden print:shadow-none print:overflow-visible border border-gray-200">
                        <div className="p-4 bg-gray-50 border-b flex justify-between items-center print:bg-white print:border-b-2 print:border-gray-400">
                            <span className="font-bold text-lg text-gray-800">Saldo Inicial: {formatCurrency(data.initialBalance, selectedCurrency)}</span>
                            <button onClick={() => window.print()} className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 hover:bg-blue-200 rounded-lg font-bold transition-colors shadow-sm print:hidden">
                                <Printer size={18} /> Imprimir Relatório
                            </button>
                        </div>
                        <table className="w-full text-sm">
                            <thead className="bg-gray-100 text-gray-700">
                                <tr>
                                    <th className="p-3 text-left border-b border-gray-300">Data</th>
                                    <th className="p-3 text-left border-b border-gray-300">Histórico</th>
                                    <th className="p-3 text-right text-green-700 border-b border-gray-300">Entrada</th>
                                    <th className="p-3 text-right text-red-700 border-b border-gray-300">Saída</th>
                                    <th className="p-3 text-right border-b border-gray-300">Saldo</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.transactions.map((t, i) => (
                                    <tr key={t.id} className="border-b hover:bg-gray-50 border-gray-200 transition-colors">
                                        <td className="p-3 text-gray-600">{formatDateUTC(t.date)}</td>
                                        <td className="p-3 font-medium text-gray-800">{t.history}</td>
                                        <td className="p-3 text-right text-green-600 font-bold">{t.entrada > 0 ? formatCurrency(t.entrada, selectedCurrency) : '-'}</td>
                                        <td className="p-3 text-right text-red-600 font-bold">{t.saida > 0 ? formatCurrency(t.saida, selectedCurrency) : '-'}</td>
                                        <td className="p-3 text-right font-black text-gray-800 bg-gray-50/50">{formatCurrency(t.saldo, selectedCurrency)}</td>
                                    </tr>
                                ))}
                                {data.transactions.length === 0 && (
                                    <tr><td colSpan="5" className="p-6 text-center text-gray-500 italic">Nenhuma movimentação neste período.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
};

// --- B. EXTRATO DE CONTA ---
const AccountStatementReport = ({ accounts, selectedCurrency, formatCurrency, users, currentUser }) => {
    const [accountId, setAccountId] = useState('');
    const [startDate, setStartDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
    const [selectedUserId, setSelectedUserId] = useState('todos');
    const [data, setData] = useState(null);
    const { apiFetch } = useApi();

    useEffect(() => {
        if (accounts && accounts.length > 0 && !accountId) {
             const activeAccount = accounts.find(a => a.currency === selectedCurrency && a.status === 'ativo');
             if (activeAccount) setAccountId(activeAccount.id);
        }
    }, [accounts, accountId, selectedCurrency]);

    const fetchReport = async (e) => {
        e.preventDefault();
        if(!accountId) return alert('Selecione uma conta');
        try {
            const userQuery = selectedUserId !== 'todos' ? `&userId=${selectedUserId}` : '';
            const res = await apiFetch(`/reports/general-ledger?accountId=${accountId}&startDate=${startDate}&endDate=${endDate}${userQuery}`);
            setData(res);
        } catch (error) { 
            console.error(error); 
            alert('Erro ao gerar extrato: ' + error.message);
        }
    };

    return (
        <div>
            <form onSubmit={fetchReport} className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-6 print:hidden bg-gray-50 p-4 rounded border border-gray-200 items-end">
                
                <div className="md:col-span-3">
                    <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Conta</label>
                    <select value={accountId} onChange={e => setAccountId(e.target.value)} className="w-full p-2 border rounded border-gray-300 text-sm outline-none focus:border-blue-500">
                        <option value="">Selecione...</option>
                        {accounts.filter(a => a.currency === selectedCurrency).map(a => (
                            <option key={a.id} value={a.id}>{a.name}</option>
                        ))}
                    </select>
                </div>
                
                <div className="md:col-span-3">
                    <UserFilterSelect users={users} selectedUserId={selectedUserId} setSelectedUserId={setSelectedUserId} currentUser={currentUser} />
                </div>
                
                <div className="md:col-span-2">
                    <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Data Início</label>
                    <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-full p-2 border rounded border-gray-300 text-sm outline-none focus:border-blue-500" />
                </div>
                
                <div className="md:col-span-2">
                    <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Data Fim</label>
                    <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-full p-2 border rounded border-gray-300 text-sm outline-none focus:border-blue-500" />
                </div>
                
                <div className="md:col-span-2">
                    <button type="submit" className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-bold shadow-sm transition-colors text-sm h-[38px]">
                        Gerar Extrato
                    </button>
                </div>
            </form>
            
            {data && (
                <>
                    <div className="flex justify-between items-center mb-4 print:hidden">
                        <h3 className="font-bold text-gray-800 text-lg border-l-4 border-blue-600 pl-3">Resultados do Extrato</h3>
                        <button onClick={() => window.print()} className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 hover:bg-blue-200 rounded-lg font-bold transition-colors shadow-sm">
                            <Printer size={18} /> Imprimir Relatório
                        </button>
                    </div>

                    <div className="hidden print:block mb-4 text-center">
                        <h2 className="text-xl font-bold uppercase tracking-wider">Extrato de Conta</h2>
                        <p className="text-sm">Conta: {accounts.find(a => String(a.id) === String(accountId))?.name}</p>
                        <p className="text-sm">Período: {startDate.split('-').reverse().join('/')} a {endDate.split('-').reverse().join('/')}</p>
                        {selectedUserId !== 'todos' && <p className="text-sm text-gray-600 mt-1">Filtrado por: {users.find(u => String(u.id) === String(selectedUserId))?.username}</p>}
                    </div>

                    <div className="bg-white rounded shadow overflow-x-auto print:shadow-none print:overflow-visible border border-gray-200">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-100 text-gray-700">
                                <tr>
                                    <th className="p-3 text-left border-b border-gray-300">Data</th>
                                    <th className="p-3 text-left border-b border-gray-300">Descrição</th>
                                    <th className="p-3 text-right border-b border-gray-300 text-green-700">Crédito</th>
                                    <th className="p-3 text-right border-b border-gray-300 text-red-700">Débito</th>
                                    <th className="p-3 text-right border-b border-gray-300">Saldo</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr className="bg-gray-50/80 font-semibold border-b border-gray-300">
                                    <td colSpan="4" className="p-3 text-right text-gray-600 uppercase tracking-wider text-xs">Saldo Anterior</td>
                                    <td className="p-3 text-right font-bold text-gray-800">{formatCurrency(data.openingBalance, selectedCurrency)}</td>
                                </tr>
                                {data.transactions.map((t, i) => (
                                    <tr key={i} className="border-b border-gray-200 hover:bg-gray-50 transition-colors">
                                        <td className="p-3 text-gray-600">{formatDateUTC(t.date)}</td>
                                        <td className="p-3 font-medium text-gray-800">{t.description}</td>
                                        <td className="p-3 text-right text-green-600 font-bold">{t.credit > 0 ? formatCurrency(t.credit, selectedCurrency) : ''}</td>
                                        <td className="p-3 text-right text-red-600 font-bold">{t.debit > 0 ? formatCurrency(t.debit, selectedCurrency) : ''}</td>
                                        <td className="p-3 text-right font-black text-gray-800 bg-gray-50/30">{formatCurrency(t.balance, selectedCurrency)}</td>
                                    </tr>
                                ))}
                                {data.transactions.length === 0 && (
                                    <tr><td colSpan="5" className="p-6 text-center text-gray-500 italic">Nenhuma movimentação neste período.</td></tr>
                                )}
                                <tr className="bg-gray-100 font-bold border-t-2 border-gray-400">
                                    <td colSpan="4" className="p-4 text-right text-gray-700 uppercase tracking-wider">Saldo Final</td>
                                    <td className="p-4 text-right text-lg text-blue-800">{formatCurrency(data.closingBalance, selectedCurrency)}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
};

// --- C. e D. CONTAS A PAGAR / RECEBER ---
const FinancialStatusReport = ({ type, formatCurrency, selectedCurrency }) => {
    const [viewType, setViewType] = useState('vencer'); 
    const [groupBy, setGroupBy] = useState('list');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [data, setData] = useState(null);
    const { apiFetch } = useApi();

    const fetchReport = async () => {
        try {
            const endpoint = type === 'payables' ? '/reports/energy/payables' : '/reports/energy/receivables';
            const res = await apiFetch(`${endpoint}?viewType=${viewType}&groupBy=${groupBy}&startDate=${startDate}&endDate=${endDate}`);
            setData(res.data);
        } catch (error) { console.error(error); alert('Erro ao buscar dados: ' + error.message); }
    };

    // Renderiza a tabela baseada se é lista ou agrupamento
    const renderTable = () => {
        if (!data) return <p className="text-gray-500 mt-4 print:hidden">Selecione os filtros e clique em "Buscar Relatório".</p>;

        return (
            <>
                <div className="flex justify-between items-center mb-4 print:hidden mt-6">
                    <h3 className="font-bold text-gray-800 text-lg border-l-4 border-blue-600 pl-3">Resultados do Relatório</h3>
                    <button onClick={() => window.print()} className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 hover:bg-blue-200 rounded-lg transition-colors font-bold shadow-sm">
                        <Printer size={18} /> Imprimir Relatório
                    </button>
                </div>

                <div className="hidden print:block mb-6 text-center border-b-2 border-gray-400 pb-4">
                    <h2 className="text-xl font-bold uppercase tracking-wider">
                        {type === 'payables' ? 'Relatório de Contas a Pagar' : 'Relatório de Contas a Receber'} - {viewType.toUpperCase()}
                    </h2>
                    <p className="text-sm mt-1">Filtros: Agrupado por {groupBy === 'list' ? 'Detalhado' : (groupBy === 'month' ? 'Mês' : 'Ano')}</p>
                    <p className="text-sm">Período: {startDate ? startDate.split('-').reverse().join('/') : 'Início'} a {endDate ? endDate.split('-').reverse().join('/') : 'Fim'}</p>
                </div>

                {groupBy === 'list' ? (
                    <div className="overflow-x-auto mt-4 print:overflow-visible border border-gray-200 rounded shadow-sm">
                        <table className="w-full text-sm bg-white print:shadow-none">
                            <thead className="bg-gray-100 text-gray-700">
                                <tr>
                                    <th className="p-3 text-left border-b border-gray-300">Descrição</th>
                                    <th className="p-3 text-left border-b border-gray-300">NF</th>
                                    <th className="p-3 text-left border-b border-gray-300">Meio Pgto</th>
                                    <th className="p-3 text-left border-b border-gray-300">Doc/Boleto</th>
                                    <th className="p-3 text-left border-b border-gray-300">Vencimento</th>
                                    {viewType === 'realizados' && <th className="p-3 text-left text-green-700 border-b border-gray-300">Pagamento</th>}
                                    <th className="p-3 text-right border-b border-gray-300">Valor</th>
                                </tr>
                            </thead>
                            <tbody>
                                {Array.isArray(data) && data.map((item, i) => (
                                    <tr key={i} className="border-b hover:bg-gray-50 border-gray-200 transition-colors">
                                        <td className="p-3 font-medium text-gray-800">{item.description}</td>
                                        <td className="p-3 text-gray-500 text-xs">{item.nf_numero || '-'}</td>
                                        <td className="p-3 text-gray-500 text-xs">{item.meio_pagamento || '-'}</td>
                                        <td className="p-3 text-gray-500 text-xs">{item.boleto_numero || '-'}</td>
                                        <td className="p-3 text-gray-700">{formatDateUTC(item.due_date)}</td>
                                        
                                        {viewType === 'realizados' && (
                                            <td className="p-3 text-green-700 font-bold bg-green-50/30">
                                                {formatDateUTC(item.payment_date)}
                                            </td>
                                        )}
                                        
                                        <td className={`p-3 text-right font-black ${type === 'payables' ? 'text-red-600' : 'text-green-600'}`}>{formatCurrency(item.amount || item.paid_amount || item.received_amount || 0, selectedCurrency)}</td>
                                    </tr>
                                ))}
                                {Array.isArray(data) && data.length === 0 && <tr><td colSpan="8" className="p-6 text-center text-gray-500 italic">Nenhum registro encontrado.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="grid gap-4 mt-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 print:grid-cols-2">
                        {Object.keys(data).map(key => (
                            <div key={key} className="bg-white p-5 rounded-lg shadow-sm border border-gray-200 border-l-4 border-l-blue-500 print:shadow-none print:border print:border-gray-300 transition-shadow hover:shadow-md">
                                <h3 className="font-black text-xl text-gray-800">{key}</h3>
                                <div className="flex justify-between items-end mt-4">
                                    <span className="text-sm font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded">Qtd: {data[key].count}</span>
                                    <span className={`text-xl font-black ${type === 'payables' ? 'text-red-600' : 'text-green-600'}`}>{formatCurrency(data[key].total, selectedCurrency)}</span>
                                </div>
                            </div>
                        ))}
                        {Object.keys(data).length === 0 && <p className="col-span-full text-center p-8 text-gray-500 italic bg-gray-50 rounded border border-gray-200">Nenhum dado encontrado para o agrupamento.</p>}
                    </div>
                )}
            </>
        );
    };

    return (
        <div>
            <div className="bg-gray-50 border border-gray-200 p-5 rounded-lg mb-4 print:hidden">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
                    
                    <div className="md:col-span-5">
                        <label className="text-xs font-bold text-gray-500 uppercase block mb-2">Situação (Status)</label>
                        <div className="flex gap-2">
                            <button onClick={() => setViewType('vencer')} className={`flex-1 py-2 px-3 rounded-lg text-sm font-bold transition-colors shadow-sm ${viewType === 'vencer' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-100'}`}>A Vencer</button>
                            <button onClick={() => setViewType('vencidos')} className={`flex-1 py-2 px-3 rounded-lg text-sm font-bold transition-colors shadow-sm ${viewType === 'vencidos' ? 'bg-red-600 text-white' : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-100'}`}>Vencidos</button>
                            <button onClick={() => setViewType('realizados')} className={`flex-1 py-2 px-3 rounded-lg text-sm font-bold transition-colors shadow-sm ${viewType === 'realizados' ? 'bg-green-600 text-white' : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-100'}`}>Realizados</button>
                        </div>
                    </div>

                    <div className="md:col-span-2">
                        <label className="text-xs font-bold text-gray-500 uppercase block mb-2">Agrupar por</label>
                        <select value={groupBy} onChange={e => setGroupBy(e.target.value)} className="w-full p-2 text-sm border border-gray-300 rounded-lg outline-none focus:border-blue-500 h-[38px]">
                            <option value="list">Detalhado (Lista)</option>
                            <option value="month">Mês</option>
                            <option value="year">Ano</option>
                        </select>
                    </div>
                    
                    <div className="md:col-span-2">
                        <label className="text-xs font-bold text-gray-500 uppercase block mb-2">Data Início</label>
                        <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-full p-2 text-sm border border-gray-300 rounded-lg outline-none focus:border-blue-500 h-[38px]" />
                    </div>

                    <div className="md:col-span-2">
                        <label className="text-xs font-bold text-gray-500 uppercase block mb-2">Data Fim</label>
                        <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-full p-2 text-sm border border-gray-300 rounded-lg outline-none focus:border-blue-500 h-[38px]" />
                    </div>

                    <div className="md:col-span-1">
                        <button onClick={fetchReport} className="w-full bg-blue-600 text-white rounded-lg font-bold shadow-sm hover:bg-blue-700 transition-colors flex items-center justify-center h-[38px]">
                            <Search size={18}/>
                        </button>
                    </div>

                </div>
            </div>
            {renderTable()}
        </div>
    );
};

// --- SUB-COMPONENTE PARA LINHAS DO DRE ---
const DRESection = ({ title, data, type, formatCurrency, selectedCurrency, signal = '-' }) => {
    const [expanded, setExpanded] = useState(false);
    
    const colorClass = type === 'revenue' ? 'text-blue-700' : 'text-red-600';
    const bgColorClass = type === 'revenue' ? 'bg-blue-50' : 'bg-red-50';

    if (!data || data.total === 0) return null;

    return (
        <div className="border-b border-gray-200 last:border-0">
            <div 
                className={`flex justify-between items-center p-3 cursor-pointer hover:bg-gray-50 transition-colors ${expanded ? 'bg-gray-50 print:bg-white' : ''}`}
                onClick={() => setExpanded(!expanded)}
            >
                <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400 print:hidden">{expanded ? '▼' : '▶'}</span>
                    <span className="text-xs text-gray-400 hidden print:inline-block">-</span>
                    <span className={`font-bold ${colorClass}`}>{title}</span>
                </div>
                <span className={`font-bold text-base ${colorClass}`}>
                    {type === 'expense' && signal} {formatCurrency(data.total, selectedCurrency)}
                </span>
            </div>
            
            {expanded && (
                <div className={`pl-8 pr-3 py-3 ${bgColorClass} text-sm space-y-2 print:bg-white border-t border-gray-100 print:border-none rounded-b-lg mb-1`}>
                    {data.items.map((item, idx) => (
                        <div key={idx} className="flex justify-between text-gray-700 border-b border-white/40 pb-1 last:border-0 last:pb-0">
                            <span className="font-medium">{item.name}</span>
                            <span className="font-bold opacity-80">{formatCurrency(item.value, selectedCurrency)}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

// --- E. DRE (DETALHADO) ---
const DREReport = ({ formatCurrency, selectedCurrency }) => {
    const [year, setYear] = useState(new Date().getFullYear());
    const [month, setMonth] = useState('');
    const [data, setData] = useState(null);
    const { apiFetch } = useApi();

    const fetchReport = async () => {
        try {
            const res = await apiFetch(`/reports/dre?year=${year}&month=${month}`);
            setData(res.data);
        } catch (error) { console.error(error); }
    };

    const receitaBruta = data?.receitas?.total || 0;
    const deducoes = data?.deducoes?.total || 0;
    const receitaLiquida = receitaBruta - deducoes;
    const custos = data?.custos?.total || 0;
    const lucroBruto = receitaLiquida - custos;
    const despesas = data?.despesas?.total || 0;
    const resultadoLiquido = lucroBruto - despesas;
    const margemLucro = receitaBruta > 0 ? ((resultadoLiquido / receitaBruta) * 100).toFixed(1) : 0;

    return (
        <div>
            <div className="flex flex-wrap gap-4 mb-6 items-end bg-gray-50 border border-gray-200 p-4 rounded-lg print:hidden">
                <div>
                    <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Mês</label>
                    <select value={month} onChange={e => setMonth(e.target.value)} className="w-40 p-2 border border-gray-300 rounded outline-none focus:border-blue-500 text-sm">
                        <option value="">Consolidado Anual</option>
                        {Array.from({length:12}, (_, i) => i + 1).map(m => (
                            <option key={m} value={m}>{new Date(0, m-1).toLocaleString('pt-BR', { month: 'long' })}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Ano Base</label>
                    <input type="number" value={year} onChange={e => setYear(e.target.value)} className="w-28 p-2 border border-gray-300 rounded outline-none focus:border-blue-500 text-sm" />
                </div>
                <button onClick={fetchReport} className="px-6 py-2 bg-blue-600 text-white rounded font-bold shadow-sm hover:bg-blue-700 h-[38px] flex items-center justify-center gap-2 transition-colors">
                    <PieChart size={16}/> Gerar DRE
                </button>
            </div>

            {data && (
                <div className="bg-white rounded-xl shadow-md max-w-4xl mx-auto border border-gray-200 print:shadow-none print:border-none print:max-w-full overflow-hidden">
                    <div className="p-4 border-b border-gray-200 bg-blue-600 text-white flex justify-between items-center print:bg-white print:text-black print:border-b-2 print:border-gray-800 relative">
                        <div className="flex-1 text-center print:text-left">
                            <h3 className="font-bold text-lg uppercase tracking-wider">Demonstração do Resultado (DRE)</h3>
                            <p className="text-xs text-blue-100 mt-1 tracking-wide">{month ? `${month.toString().padStart(2, '0')}/` : 'Consolidado '}{year}</p>
                        </div>
                        <button onClick={() => window.print()} className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-2 px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded transition-colors font-bold shadow-sm print:hidden">
                            <Printer size={16} />
                        </button>
                    </div>
                    
                    <div className="p-0 print:p-0 print:mt-4">
                        <DRESection title="(+) Receita Bruta de Serviços" data={data.receitas} type="revenue" formatCurrency={formatCurrency} selectedCurrency={selectedCurrency} />
                        <DRESection title="(-) Impostos e Deduções" data={data.deducoes} type="expense" formatCurrency={formatCurrency} selectedCurrency={selectedCurrency} />
                        
                        <div className="flex justify-between items-center font-bold border-y-2 border-blue-100 py-3 px-4 bg-blue-50/50 print:bg-white print:border-gray-800 print:border-t-2 print:border-b-0">
                            <span className="text-blue-900 tracking-wide text-sm">(=) RECEITA LÍQUIDA</span>
                            <span className="text-base text-blue-900">{formatCurrency(receitaLiquida, selectedCurrency)}</span>
                        </div>

                        <DRESection title="(-) Custos Diretos (Fornecedores/Materiais)" data={data.custos} type="expense" formatCurrency={formatCurrency} selectedCurrency={selectedCurrency} />
                        
                        <div className="flex justify-between items-center font-bold border-y-2 border-indigo-100 py-3 px-4 bg-indigo-50/50 print:bg-white print:border-gray-800 print:border-t-2 print:border-b-0">
                            <span className="text-indigo-900 tracking-wide text-sm">(=) LUCRO BRUTO</span>
                            <span className="text-base text-indigo-900">{formatCurrency(lucroBruto, selectedCurrency)}</span>
                        </div>

                        <DRESection title="(-) Despesas Operacionais / Administrativas" data={data.despesas} type="expense" formatCurrency={formatCurrency} selectedCurrency={selectedCurrency} />

                        <div className={`flex justify-between items-center font-bold text-lg p-4 print:border-t-2 print:border-gray-800 print:bg-white print:text-black ${resultadoLiquido >= 0 ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}>
                            <div className="flex flex-col">
                                <span className="tracking-wide">(=) RESULTADO LÍQUIDO FINAL</span>
                                <span className="text-xs font-medium opacity-90 mt-1 uppercase tracking-wider">Margem Líquida Real: {margemLucro}%</span>
                            </div>
                            <span>{formatCurrency(resultadoLiquido, selectedCurrency)}</span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// --- F. SERVIÇOS POR CLIENTE ---
const ServicesByClientReport = ({ formatCurrency, selectedCurrency }) => {
    const [clients, setClients] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedClient, setSelectedClient] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const wrapperRef = useRef(null); 
    
    // Estado para Abas
    const [activeTab, setActiveTab] = useState('servicos'); // 'servicos' ou 'financeiro'

    const [year, setYear] = useState(new Date().getFullYear());
    const [month, setMonth] = useState('');
    
    const [reportData, setReportData] = useState(null);
    const { apiFetch } = useApi();

    // Carrega clientes automaticamente ao montar
    useEffect(() => {
        apiFetch('/reports/energy/services-client').then(res => {
            if(res && res.clients) setClients(res.clients);
        });
    }, [apiFetch]);

    // Fecha sugestões ao clicar fora
    useEffect(() => {
        function handleClickOutside(event) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setShowSuggestions(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => { document.removeEventListener("mousedown", handleClickOutside); };
    }, [wrapperRef]);

    const fetchReport = async () => {
        if (!selectedClient) return alert('Pesquise e selecione um cliente na lista primeiro.');
        try {
            const res = await apiFetch(`/reports/energy/services-client?clientId=${selectedClient}&year=${year}&month=${month}`);
            setReportData(res);
        } catch (error) { console.error(error); }
    };

    // --- FILTRO: NOME, CPF, CNPJ (COM E SEM PONTUAÇÃO) ---
    const filteredClients = useMemo(() => {
        if (!searchTerm) return clients;
        const term = searchTerm.toLowerCase();
        // Remove tudo que não é número da busca para comparar com CPF/CNPJ limpo
        const termNumeric = term.replace(/\D/g, '');

        return clients.filter(c => {
            const nameMatch = c.name.toLowerCase().includes(term);
            let docMatch = false;

            if (c.document_id) {
                // Remove formatação do documento salvo no banco para comparar
                const docNumeric = c.document_id.replace(/\D/g, '');
                
                // Verifica se o termo existe no documento original OU se o termo numérico existe no documento limpo
                docMatch = c.document_id.includes(term) || (termNumeric.length > 0 && docNumeric.includes(termNumeric));
            }

            return nameMatch || docMatch;
        });
    }, [clients, searchTerm]);

    const handleSelectClient = (client) => {
        setSelectedClient(client.id);
        setSearchTerm(`${client.name} ${client.document_id ? `(${client.document_id})` : ''}`);
        setShowSuggestions(false);
    };

    return (
        <div>
            {/* Filtros */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-6 bg-gray-50 p-5 rounded-lg border border-gray-200 items-end print:hidden">
                <div className="md:col-span-5 relative" ref={wrapperRef}>
                    <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Pesquisar Cliente</label>
                    <div className="relative">
                        <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
                        <input 
                            type="text" 
                            placeholder="Digite Nome, CPF ou CNPJ..." 
                            value={searchTerm} 
                            onChange={e => {
                                setSearchTerm(e.target.value);
                                setSelectedClient(''); 
                                setShowSuggestions(true);
                            }}
                            onFocus={() => setShowSuggestions(true)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-sm"
                        />
                    </div>
                    {showSuggestions && searchTerm.length > 0 && (
                        <ul className="absolute z-20 w-full bg-white border border-gray-200 rounded-lg shadow-xl max-h-64 overflow-y-auto mt-1 top-full left-0">
                            {filteredClients.map(c => (
                                <li 
                                    key={c.id} 
                                    onClick={() => handleSelectClient(c)}
                                    className="p-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                                >
                                    <div className="font-bold text-gray-800 text-sm">{c.name}</div>
                                    {c.document_id && <div className="text-xs font-medium text-gray-400 mt-0.5 tracking-wide">{c.document_id}</div>}
                                </li>
                            ))}
                            {filteredClients.length === 0 && (
                                <li className="p-4 text-center text-gray-500 text-sm italic bg-gray-50">Nenhum cliente encontrado.</li>
                            )}
                        </ul>
                    )}
                </div>

                <div className="md:col-span-3">
                     <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Filtrar por Mês (Opcional)</label>
                     <select value={month} onChange={e => setMonth(e.target.value)} className="w-full p-2 border border-gray-300 rounded-lg outline-none focus:border-blue-500 text-sm">
                        <option value="">Consolidado Anual</option>
                        {Array.from({length:12}, (_, i) => i + 1).map(m => (
                            <option key={m} value={m}>{new Date(0, m-1).toLocaleString('pt-BR', { month: 'long' })}</option>
                        ))}
                    </select>
                </div>

                <div className="md:col-span-2">
                     <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Ano</label>
                     <input type="number" value={year} onChange={e => setYear(e.target.value)} className="w-full p-2 border border-gray-300 rounded-lg outline-none focus:border-blue-500 text-sm" />
                </div>

                <div className="md:col-span-2">
                    <button onClick={fetchReport} className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 shadow-sm transition-colors flex items-center justify-center gap-2 h-[38px]">
                        <Search size={16}/> Buscar
                    </button>
                </div>
            </div>

            {reportData && (
                <div className="space-y-6 animate-in fade-in zoom-in-95 duration-300">
                    
                    <div className="flex justify-between items-center print:hidden border-l-4 border-blue-600 pl-3">
                        <h2 className="text-xl font-bold text-gray-800">Dossiê do Cliente</h2>
                        <button onClick={() => window.print()} className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 hover:bg-blue-200 rounded-lg transition-colors font-bold shadow-sm">
                            <Printer size={18} /> Imprimir Dossiê Completo
                        </button>
                    </div>
                    
                    {/* Cabeçalho de Impressão */}
                    <div className="hidden print:block border-b-4 border-gray-800 pb-4 mb-6 text-center">
                        <h2 className="text-2xl font-black uppercase tracking-wider">Dossiê Completo de Serviços e Posição Financeira</h2>
                        <p className="text-lg mt-2 font-bold text-gray-700">{searchTerm.split('(')[0]}</p>
                        <p className="text-sm font-medium text-gray-500 mt-1">Período Referência: {month ? `${month.toString().padStart(2,'0')}/${year}` : `Consolidado de ${year}`}</p>
                    </div>

                    {/* 1. RESUMO (CARDS) */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 print:grid-cols-3">
                        <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-200 border-l-4 border-l-blue-600 print:shadow-none print:border print:border-gray-300">
                            <div className="flex justify-between items-start">
                                <div>
                                    <p className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-2">Serviços Faturados</p>
                                    <h3 className="text-2xl font-black text-blue-800 print:text-black">{formatCurrency(reportData.summary.totalServicos, selectedCurrency)}</h3>
                                </div>
                                <div className="p-3 bg-blue-50 rounded-lg print:hidden"><FileText className="text-blue-600" size={24} /></div>
                            </div>
                        </div>

                        <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-200 border-l-4 border-l-red-500 print:shadow-none print:border print:border-gray-300">
                            <div className="flex justify-between items-start">
                                <div>
                                    <p className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-2">Atrasado / Vencido</p>
                                    <h3 className="text-2xl font-black text-red-600 print:text-black">{formatCurrency(reportData.summary.totalVencido, selectedCurrency)}</h3>
                                </div>
                                <div className="p-3 bg-red-50 rounded-lg print:hidden"><AlertTriangle className="text-red-600" size={24} /></div>
                            </div>
                        </div>

                        <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-200 border-l-4 border-l-yellow-500 print:shadow-none print:border print:border-gray-300">
                            <div className="flex justify-between items-start">
                                <div>
                                    <p className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-2">A Vencer (Futuro)</p>
                                    <h3 className="text-2xl font-black text-yellow-600 print:text-black">{formatCurrency(reportData.summary.totalAVencer, selectedCurrency)}</h3>
                                </div>
                                <div className="p-3 bg-yellow-50 rounded-lg print:hidden"><Clock className="text-yellow-600" size={24} /></div>
                            </div>
                        </div>
                    </div>

                    {/* 2. NAVEGAÇÃO POR ABAS */}
                    <div className="border-b border-gray-200 print:hidden mt-8">
                        <nav className="-mb-px flex gap-2">
                            <button
                                onClick={() => setActiveTab('servicos')}
                                className={`py-3 px-6 border-b-2 font-bold text-sm transition-colors rounded-t-lg ${
                                    activeTab === 'servicos'
                                        ? 'border-blue-600 text-blue-700 bg-blue-50/50'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                                }`}
                            >
                                Detalhamento de Serviços (OS/NSA)
                            </button>
                            <button
                                onClick={() => setActiveTab('financeiro')}
                                className={`py-3 px-6 border-b-2 font-bold text-sm transition-colors rounded-t-lg ${
                                    activeTab === 'financeiro'
                                        ? 'border-blue-600 text-blue-700 bg-blue-50/50'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                                }`}
                            >
                                Posição Financeira Aberta
                            </button>
                        </nav>
                    </div>

                    {/* CONTEÚDO DAS ABAS */}
                    {activeTab === 'servicos' && (
                        <div className="animate-in fade-in slide-in-from-bottom-2">
                            <div className="hidden print:block mt-8 mb-4">
                                <h3 className="text-xl font-bold border-b-2 border-gray-300 pb-2 text-gray-800 flex items-center gap-2">
                                    <FileText size={20}/> 1. Relação de Serviços Executados
                                </h3>
                            </div>
                            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden print:shadow-none print:border-none print:mt-4">
                                <table className="w-full text-sm text-left">
                                    <thead className="bg-gray-100 text-gray-700 print:bg-gray-200 border-b border-gray-300">
                                        <tr>
                                            <th className="p-3 font-bold">Abertura</th>
                                            <th className="p-3 font-bold">Fechamento/Faturamento</th>
                                            <th className="p-3 font-bold">Cód. Serviço (OS/NSA)</th>
                                            <th className="p-3 font-bold">Situação</th>
                                            <th className="p-3 font-bold text-right">Valor Negociado</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {reportData.services.map(s => (
                                            <tr key={s.id} className="hover:bg-gray-50 transition-colors">
                                                <td className="p-3 text-gray-600">{formatDateUTC(s.data_abertura)}</td>
                                                <td className="p-3 text-gray-800 font-medium">{s.data_faturamento ? formatDateUTC(s.data_faturamento) : '-'}</td>
                                                <td className="p-3 font-bold text-blue-700">{s.os_number}</td>
                                                <td className="p-3">
                                                    <span className="px-2.5 py-1 bg-green-100 text-green-800 font-bold rounded text-xs tracking-wide print:bg-transparent print:border print:border-gray-400">
                                                        {s.status.toUpperCase()}
                                                    </span>
                                                </td>
                                                <td className="p-3 text-right font-black text-gray-800">{formatCurrency(s.valor_total, selectedCurrency)}</td>
                                            </tr>
                                        ))}
                                        {reportData.services.length === 0 && (
                                            <tr><td colSpan="5" className="p-8 text-center text-gray-500 italic bg-gray-50">Nenhum serviço faturado encontrado no período selecionado.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {activeTab === 'financeiro' && (
                        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2">
                            
                            <div className="hidden print:block mt-8 mb-4">
                                <h3 className="text-xl font-bold border-b-2 border-gray-300 pb-2 text-gray-800 flex items-center gap-2">
                                    <DollarSign size={20}/> 2. Espelho de Débitos Abertos
                                </h3>
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 print:grid-cols-1 print:gap-12">
                                {/* VENCIDAS */}
                                <div>
                                    <h3 className="font-bold text-red-700 text-lg mb-4 flex items-center gap-2 uppercase tracking-wider border-b-2 border-red-200 pb-2 print:text-black print:border-gray-400">
                                        <AlertTriangle size={20} className="print:hidden"/> Faturas Vencidas (Inadimplência)
                                    </h3>
                                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden print:shadow-none print:border-none">
                                        <table className="w-full text-sm text-left">
                                            <thead className="bg-red-50 text-red-900 border-b border-red-100 print:bg-gray-100 print:text-gray-800 print:border-gray-300">
                                                <tr>
                                                    <th className="p-3 font-bold">Vencimento Original</th>
                                                    <th className="p-3 font-bold">Ref. Documento</th>
                                                    <th className="p-3 font-bold text-right">Valor em Aberto</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100">
                                                {reportData.invoices.vencidas.map(inv => (
                                                    <tr key={inv.id} className="hover:bg-red-50/50 transition-colors">
                                                        <td className="p-3 text-red-700 font-bold print:text-gray-800">{formatDateUTC(inv.data_vencimento)}</td>
                                                        <td className="p-3 text-gray-700 font-medium">NF {inv.nf_numero || 'S/N'} • Parc {inv.numero_parcela}</td>
                                                        <td className="p-3 text-right font-black text-red-600 print:text-black">{formatCurrency(inv.valor_parcela, selectedCurrency)}</td>
                                                    </tr>
                                                ))}
                                                {reportData.invoices.vencidas.length === 0 && (
                                                    <tr><td colSpan="3" className="p-6 text-center text-gray-500 italic bg-gray-50">Nenhuma pendência vencida registrada.</td></tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                {/* A VENCER */}
                                <div>
                                    <h3 className="font-bold text-yellow-700 text-lg mb-4 flex items-center gap-2 uppercase tracking-wider border-b-2 border-yellow-200 pb-2 print:text-black print:border-gray-400">
                                        <Clock size={20} className="print:hidden"/> Previsão A Vencer
                                    </h3>
                                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden print:shadow-none print:border-none">
                                        <table className="w-full text-sm text-left">
                                            <thead className="bg-yellow-50 text-yellow-900 border-b border-yellow-100 print:bg-gray-100 print:text-gray-800 print:border-gray-300">
                                                <tr>
                                                    <th className="p-3 font-bold">Data Programada</th>
                                                    <th className="p-3 font-bold">Ref. Documento</th>
                                                    <th className="p-3 font-bold text-right">Valor Previsto</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100">
                                                {reportData.invoices.aVencer.map(inv => (
                                                    <tr key={inv.id} className="hover:bg-yellow-50/50 transition-colors">
                                                        <td className="p-3 text-gray-700 font-bold">{formatDateUTC(inv.data_vencimento)}</td>
                                                        <td className="p-3 text-gray-700 font-medium">NF {inv.nf_numero || 'S/N'} • Parc {inv.numero_parcela}</td>
                                                        <td className="p-3 text-right font-black text-yellow-700 print:text-black">{formatCurrency(inv.valor_parcela, selectedCurrency)}</td>
                                                    </tr>
                                                ))}
                                                {reportData.invoices.aVencer.length === 0 && (
                                                    <tr><td colSpan="3" className="p-6 text-center text-gray-500 italic bg-gray-50">Não há previsões a vencer.</td></tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// --- G. CONTROLE DE FROTA E COMBUSTÍVEL ---
const FleetFuelReport = ({ formatCurrency, selectedCurrency, users, currentUser }) => {
    const [month, setMonth] = useState('');
    const [year, setYear] = useState(new Date().getFullYear());
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [selectedUserId, setSelectedUserId] = useState('todos');
    const [data, setData] = useState(null);
    const { apiFetch } = useApi();

    const fetchReport = async (e) => {
        if(e) e.preventDefault();
        try {
            const userQuery = selectedUserId !== 'todos' ? `&userId=${selectedUserId}` : '';
            const params = new URLSearchParams();
            if (month && year && !startDate && !endDate) {
                params.append('month', `${year}-${month.toString().padStart(2, '0')}`);
            } else if (!month && year && !startDate && !endDate) {
                params.append('year', year);
            }
            
            if (startDate) params.append('startDate', startDate);
            if (endDate) params.append('endDate', endDate);
            if (userQuery) params.append('userId', selectedUserId);

            const res = await apiFetch(`/reports/fleet-fuel?${params.toString()}`);
            setData(res);
        } catch (error) { 
            console.error(error);
            alert("Erro ao buscar relatório da Frota.");
        }
    };

    // Limpar Datas Livres se escolher Mês/Ano
    const handleMonthChange = (e) => {
        setMonth(e.target.value);
        if (e.target.value) { setStartDate(''); setEndDate(''); }
    };
    const handleYearChange = (e) => {
        setYear(e.target.value);
        if (e.target.value) { setStartDate(''); setEndDate(''); }
    };
    
    // Limpar Mês se usar Datas Específicas
    const handleStartDateChange = (e) => {
        setStartDate(e.target.value);
        if (e.target.value) setMonth('');
    };
    const handleEndDateChange = (e) => {
        setEndDate(e.target.value);
        if (e.target.value) setMonth('');
    };

    return (
        <div>
            {/* Filtros */}
            <form onSubmit={fetchReport} className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-6 print:hidden bg-gray-50 p-4 rounded border border-gray-200 items-end">
                <div className="md:col-span-3">
                    <UserFilterSelect users={users} selectedUserId={selectedUserId} setSelectedUserId={setSelectedUserId} currentUser={currentUser} />
                </div>
                
                <div className="md:col-span-2">
                     <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Mês (Opcional)</label>
                     <select value={month} onChange={handleMonthChange} className="w-full p-2 border border-gray-300 rounded outline-none focus:border-blue-500 text-sm">
                        <option value="">Anual / Livre</option>
                        {Array.from({length:12}, (_, i) => i + 1).map(m => (
                            <option key={m} value={m}>{new Date(0, m-1).toLocaleString('pt-BR', { month: 'short' })}</option>
                        ))}
                    </select>
                </div>
                <div className="md:col-span-2">
                     <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Ano</label>
                     <input type="number" value={year} onChange={handleYearChange} className="w-full p-2 border border-gray-300 rounded outline-none focus:border-blue-500 text-sm" />
                </div>

                <div className="md:col-span-2">
                    <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Data Início Livre</label>
                    <input type="date" value={startDate} onChange={handleStartDateChange} className="w-full p-2 border rounded border-gray-300 text-sm outline-none focus:border-blue-500" />
                </div>
                <div className="md:col-span-2">
                    <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Data Fim Livre</label>
                    <input type="date" value={endDate} onChange={handleEndDateChange} className="w-full p-2 border rounded border-gray-300 text-sm outline-none focus:border-blue-500" />
                </div>
                
                <div className="md:col-span-1">
                    <button type="submit" className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-bold shadow-sm transition-colors text-sm h-[38px] flex items-center justify-center">
                        <Search size={16} />
                    </button>
                </div>
            </form>

            {data && (
                <div className="animate-in fade-in slide-in-from-bottom-2 duration-300 space-y-6">
                    
                    <div className="flex justify-between items-center print:hidden border-l-4 border-blue-600 pl-3">
                        <h2 className="text-xl font-bold text-gray-800">Relatório de Custos da Frota</h2>
                        <button onClick={() => window.print()} className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 hover:bg-blue-200 rounded-lg transition-colors font-bold shadow-sm">
                            <Printer size={18} /> Imprimir Relatório
                        </button>
                    </div>

                    <div className="hidden print:block mb-6 text-center border-b-4 border-gray-800 pb-4">
                        <h2 className="text-2xl font-black uppercase tracking-wider">Consolidado de Custos da Frota</h2>
                        <p className="text-sm font-medium text-gray-500 mt-2">
                            Período Referência: {startDate && endDate ? `${startDate.split('-').reverse().join('/')} a ${endDate.split('-').reverse().join('/')}` : (month ? `${month.toString().padStart(2,'0')}/${year}` : `Ano de ${year}`)}
                        </p>
                        {selectedUserId !== 'todos' && <p className="text-sm text-gray-700 font-bold mt-1">Filtrado por Funcionário: {users.find(u => String(u.id) === String(selectedUserId))?.username}</p>}
                    </div>

                    {/* Resumo Geral (Cards) */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 print:grid-cols-2">
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 border-l-4 border-l-red-600 print:shadow-none print:border print:border-gray-300">
                            <div className="flex justify-between items-start">
                                <div>
                                    <p className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-2">Total Gasto com Combustível</p>
                                    <h3 className="text-3xl font-black text-red-600 print:text-black">{formatCurrency(data.summary.totalAmount, selectedCurrency)}</h3>
                                </div>
                                <div className="p-3 bg-red-50 rounded-lg print:hidden"><DollarSign className="text-red-600" size={24} /></div>
                            </div>
                            <div className="mt-4 pt-4 border-t border-gray-100 flex gap-4 text-sm text-gray-600 font-medium">
                                <span><strong className="text-gray-900">{data.summary.totalAbastecimentos}</strong> Lançamentos efetuados.</span>
                            </div>
                        </div>

                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 border-l-4 border-l-green-600 print:shadow-none print:border print:border-gray-300">
                            <div className="flex justify-between items-start">
                                <div>
                                    <p className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-2">Quilometragem Total Rodada</p>
                                    <h3 className="text-3xl font-black text-green-700 print:text-black">{data.summary.totalKm.toLocaleString('pt-BR')} <span className="text-lg">KM</span></h3>
                                </div>
                                <div className="p-3 bg-green-50 rounded-lg print:hidden"><Activity className="text-green-600" size={24} /></div>
                            </div>
                            <div className="mt-4 pt-4 border-t border-gray-100 flex gap-4 text-sm text-gray-600 font-medium">
                                <span>Calculada de forma precisa com base na Equipe.</span>
                            </div>
                        </div>
                    </div>

                    {/* Agrupamento por Equipe */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden print:shadow-none print:border print:border-gray-300 mt-8">
                        <div className="p-4 bg-gray-50 border-b border-gray-200 flex items-center gap-2 print:bg-gray-100 print:border-gray-400">
                            <Users className="text-blue-600" size={20}/>
                            <h3 className="font-bold text-gray-800 text-lg uppercase tracking-wider text-sm">Gastos Consolidados por Equipe / Veículo</h3>
                        </div>
                        <table className="w-full text-sm text-left">
                            <thead className="bg-gray-50/50 text-gray-500 uppercase text-xs font-bold border-b border-gray-200">
                                <tr>
                                    <th className="p-4">Nome da Equipe / Veículo</th>
                                    <th className="p-4 text-center">Nº Abastecimentos</th>
                                    <th className="p-4 text-right">Quilometragem Somada</th>
                                    <th className="p-4 text-right text-red-700">Custo Total</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {data.groupedByTeam.map((team, idx) => (
                                    <tr key={idx} className="hover:bg-blue-50/30 transition-colors">
                                        <td className="p-4 font-bold text-gray-800">{team.name}</td>
                                        <td className="p-4 text-center font-medium text-gray-600">{team.count}</td>
                                        <td className="p-4 text-right font-mono text-gray-700">
                                            <div>{team.km.toLocaleString('pt-BR')} km</div>
                                            {team.hasPending && (
                                                <div className="text-[10px] text-orange-600 font-bold leading-tight mt-1 flex justify-end">
                                                    <span className="bg-orange-100 px-1.5 py-0.5 rounded">* Troca de veículo pendente</span>
                                                </div>
                                            )}
                                        </td>
                                        <td className="p-4 text-right font-black text-red-600">{formatCurrency(team.amount, selectedCurrency)}</td>
                                    </tr>
                                ))}
                                {data.groupedByTeam.length === 0 && <tr><td colSpan="4" className="p-8 text-center text-gray-500 italic">Sem registros para esta equipe.</td></tr>}
                            </tbody>
                        </table>
                    </div>

                    {/* Agrupamento por Placa */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden print:shadow-none print:border print:border-gray-300 mt-8 page-break-inside-avoid">
                        <div className="p-4 bg-gray-50 border-b border-gray-200 flex items-center gap-2 print:bg-gray-100 print:border-gray-400">
                            <Hash className="text-orange-500" size={20}/>
                            <h3 className="font-bold text-gray-800 text-lg uppercase tracking-wider text-sm">Desempenho Físico por Veículo (Placa)</h3>
                        </div>
                        <table className="w-full text-sm text-left">
                            <thead className="bg-gray-50/50 text-gray-500 uppercase text-xs font-bold border-b border-gray-200">
                                <tr>
                                    <th className="p-4">Placa do Veículo</th>
                                    <th className="p-4 text-center">Nº Abastecimentos</th>
                                    <th className="p-4 text-right">Quilometragem Somada</th>
                                    <th className="p-4 text-right text-red-700">Custo Total</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {data.groupedByPlate && data.groupedByPlate.map((plate, idx) => (
                                    <tr key={idx} className="hover:bg-orange-50/30 transition-colors">
                                        <td className="p-4 font-bold text-gray-800 uppercase tracking-wider">{plate.name}</td>
                                        <td className="p-4 text-center font-medium text-gray-600">{plate.count}</td>
                                        <td className="p-4 text-right font-mono text-gray-700">
                                            <div>{plate.km.toLocaleString('pt-BR')} km</div>
                                            {plate.hasPending && (
                                                <div className="text-[10px] text-orange-600 font-bold leading-tight mt-1 flex justify-end">
                                                    <span className="bg-orange-100 px-1.5 py-0.5 rounded">* Troca de painel/zeragem detectada</span>
                                                </div>
                                            )}
                                        </td>
                                        <td className="p-4 text-right font-black text-red-600">{formatCurrency(plate.amount, selectedCurrency)}</td>
                                    </tr>
                                ))}
                                {(!data.groupedByPlate || data.groupedByPlate.length === 0) && <tr><td colSpan="4" className="p-8 text-center text-gray-500 italic">Sem registros por placa no período.</td></tr>}
                            </tbody>
                        </table>
                    </div>

                    {/* Agrupamento por Posto */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden print:shadow-none print:border print:border-gray-300 mt-8 page-break-inside-avoid">
                        <div className="p-4 bg-gray-50 border-b border-gray-200 flex items-center gap-2 print:bg-gray-100 print:border-gray-400">
                            <MapPin className="text-red-600" size={20}/>
                            <h3 className="font-bold text-gray-800 text-lg uppercase tracking-wider text-sm">Onde o dinheiro foi gasto (Por Posto)</h3>
                        </div>
                        <table className="w-full text-sm text-left">
                            <thead className="bg-gray-50/50 text-gray-500 uppercase text-xs font-bold border-b border-gray-200">
                                <tr>
                                    <th className="p-4">Nome do Posto de Combustível</th>
                                    <th className="p-4 text-center">Frequência (Vezes)</th>
                                    <th className="p-4 text-right text-red-700">Volume Gasto</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {data.groupedByStation.map((station, idx) => (
                                    <tr key={idx} className="hover:bg-red-50/30 transition-colors">
                                        <td className="p-4 font-bold text-gray-800">{station.name}</td>
                                        <td className="p-4 text-center font-medium text-gray-600">{station.count}</td>
                                        <td className="p-4 text-right font-black text-red-600">{formatCurrency(station.amount, selectedCurrency)}</td>
                                    </tr>
                                ))}
                                {data.groupedByStation.length === 0 && <tr><td colSpan="3" className="p-8 text-center text-gray-500 italic">Sem registros de postos.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

// --- H. PAGAMENTO DE COMISSÕES (NOVO) ---
const CommissionsReport = ({ formatCurrency, selectedCurrency, users }) => {
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [selectedUserId, setSelectedUserId] = useState('');
    const [data, setData] = useState(null);
    const { apiFetch } = useApi();

    const fetchReport = async (e) => {
        if(e) e.preventDefault();
        if (!selectedUserId) return alert("Selecione um usuário beneficiário.");
        if (!startDate || !endDate) return alert("Selecione o período completo (Data Início e Fim).");

        try {
            const res = await apiFetch(`/reports/commissions?userId=${selectedUserId}&startDate=${startDate}&endDate=${endDate}`);
            setData(res);
        } catch (error) { 
            console.error(error);
            alert("Erro ao buscar relatório de comissões.");
        }
    };

    return (
        <div>
            {/* Filtros */}
            <form onSubmit={fetchReport} className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-6 print:hidden bg-gray-50 p-4 rounded border border-gray-200 items-end">
                <div className="md:col-span-4">
                    <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Usuário Beneficiário</label>
                    <select 
                        value={selectedUserId} 
                        onChange={e => setSelectedUserId(e.target.value)} 
                        className="w-full p-2 border rounded border-gray-300 text-sm outline-none focus:border-blue-500 bg-white"
                        required
                    >
                        <option value="">-- Selecione o usuário --</option>
                        {users.map(u => (
                            <option key={u.id} value={u.id}>{u.full_name ? `${u.full_name} (${u.username})` : u.username}</option>
                        ))}
                    </select>
                </div>
                
                <div className="md:col-span-3">
                    <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Data Início</label>
                    <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} required className="w-full p-2 border rounded border-gray-300 text-sm outline-none focus:border-blue-500" />
                </div>
                
                <div className="md:col-span-3">
                    <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Data Fim</label>
                    <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} required className="w-full p-2 border rounded border-gray-300 text-sm outline-none focus:border-blue-500" />
                </div>
                
                <div className="md:col-span-2">
                    <button type="submit" className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-bold shadow-sm transition-colors text-sm h-[38px] flex items-center justify-center gap-2">
                        <Search size={16} /> Buscar
                    </button>
                </div>
            </form>

            {data && (
                <div className="animate-in fade-in slide-in-from-bottom-2 duration-300 space-y-6">
                    
                    <div className="flex justify-between items-center print:hidden border-l-4 border-blue-600 pl-3">
                        <h2 className="text-xl font-bold text-gray-800">Relatório de Pagamento de Comissões</h2>
                        <button onClick={() => window.print()} className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 hover:bg-blue-200 rounded-lg transition-colors font-bold shadow-sm">
                            <Printer size={18} /> Imprimir Relatório
                        </button>
                    </div>

                    {/* Cabeçalho de Impressão */}
                    <div className="hidden print:block mb-6 text-center border-b-4 border-gray-800 pb-4">
                        <h2 className="text-2xl font-black uppercase tracking-wider">Detalhamento de Comissões</h2>
                        <p className="text-lg mt-2 font-bold text-gray-700">Beneficiário: {users.find(u => String(u.id) === String(selectedUserId))?.full_name || users.find(u => String(u.id) === String(selectedUserId))?.username}</p>
                        <p className="text-sm font-medium text-gray-500 mt-1">Período: {startDate.split('-').reverse().join('/')} a {endDate.split('-').reverse().join('/')}</p>
                    </div>

                    {/* Resumo Geral (Cards) */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 print:grid-cols-4">
                        <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-200 border-l-4 border-l-gray-500 print:shadow-none print:border print:border-gray-300">
                            <p className="text-[10px] text-gray-500 uppercase font-bold tracking-wider mb-1">Total de OS Faturadas</p>
                            <h3 className="text-2xl font-black text-gray-800 print:text-black">{data.summary.totalOS}</h3>
                        </div>
                        <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-200 border-l-4 border-l-blue-500 print:shadow-none print:border print:border-gray-300">
                            <p className="text-[10px] text-gray-500 uppercase font-bold tracking-wider mb-1">Faturamento Bruto</p>
                            <h3 className="text-xl font-black text-blue-700 print:text-black">{formatCurrency(data.summary.totalValue, selectedCurrency)}</h3>
                        </div>
                        <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-200 border-l-4 border-l-red-500 print:shadow-none print:border print:border-gray-300">
                            <p className="text-[10px] text-gray-500 uppercase font-bold tracking-wider mb-1">Impostos Descontados</p>
                            <h3 className="text-xl font-black text-red-600 print:text-black">{formatCurrency(data.summary.totalTax, selectedCurrency)}</h3>
                        </div>
                        <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-200 border-l-4 border-l-green-500 print:shadow-none print:border print:border-gray-300 bg-green-50/30">
                            <p className="text-[10px] text-green-700 uppercase font-bold tracking-wider mb-1">Comissão a Pagar</p>
                            <h3 className="text-2xl font-black text-green-700 print:text-black">{formatCurrency(data.summary.totalCommission, selectedCurrency)}</h3>
                        </div>
                    </div>

                    {/* Tabela Detalhada */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden print:shadow-none print:border print:border-gray-300 mt-8 page-break-inside-avoid">
                        <div className="p-4 bg-gray-50 border-b border-gray-200 flex items-center gap-2 print:bg-gray-100 print:border-gray-400">
                            <List className="text-blue-600" size={20}/>
                            <h3 className="font-bold text-gray-800 text-lg uppercase tracking-wider text-sm">Detalhamento por Ordem de Serviço</h3>
                        </div>
                        <table className="w-full text-sm text-left">
                            <thead className="bg-gray-50/50 text-gray-500 uppercase text-[10px] font-bold border-b border-gray-200">
                                <tr>
                                    <th className="p-3">Data</th>
                                    <th className="p-3">OS/NSA</th>
                                    <th className="p-3">Cliente</th>
                                    <th className="p-3 text-right">Valor Bruto</th>
                                    <th className="p-3 text-right text-red-700">Imposto</th>
                                    <th className="p-3 text-right bg-gray-100">Base Cálculo</th>
                                    <th className="p-3 text-right text-green-700">Comissão</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 text-xs">
                                {data.services.map((os, idx) => (
                                    <tr key={idx} className="hover:bg-blue-50/30 transition-colors">
                                        <td className="p-3 text-gray-600">{formatDateUTC(os.data_abertura)}</td>
                                        <td className="p-3 font-bold text-blue-700">{os.os_number}</td>
                                        <td className="p-3 font-medium text-gray-800 truncate max-w-[150px]" title={os.cliente_nome}>{os.cliente_nome}</td>
                                        <td className="p-3 text-right font-medium">{formatCurrency(os.valor_original, selectedCurrency)}</td>
                                        <td className="p-3 text-right text-red-600">
                                            <div className="flex flex-col">
                                                <span>{formatCurrency(os.valor_imposto, selectedCurrency)}</span>
                                                <span className="text-[9px] opacity-70">({os.tax_percent}%)</span>
                                            </div>
                                        </td>
                                        <td className="p-3 text-right font-medium bg-gray-50">{formatCurrency(os.base_calculo, selectedCurrency)}</td>
                                        <td className="p-3 text-right font-bold text-green-700 bg-green-50/30">
                                            <div className="flex flex-col">
                                                <span>{formatCurrency(os.valor_comissao, selectedCurrency)}</span>
                                                <span className="text-[9px] opacity-70">({os.commission_percent}%)</span>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {data.services.length === 0 && (
                                    <tr>
                                        <td colSpan="7" className="p-8 text-center text-gray-500 italic">
                                            Nenhum serviço faturado/liberado para os clientes deste usuário no período.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                            {data.services.length > 0 && (
                                <tfoot className="bg-gray-100 font-bold border-t-2 border-gray-300">
                                    <tr>
                                        <td colSpan="3" className="p-3 text-right text-gray-600 uppercase text-xs">TOTAIS</td>
                                        <td className="p-3 text-right text-gray-800">{formatCurrency(data.summary.totalValue, selectedCurrency)}</td>
                                        <td className="p-3 text-right text-red-600">{formatCurrency(data.summary.totalTax, selectedCurrency)}</td>
                                        <td className="p-3 text-right bg-gray-200"></td>
                                        <td className="p-3 text-right text-green-700 text-sm">{formatCurrency(data.summary.totalCommission, selectedCurrency)}</td>
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};


// --- PÁGINA PRINCIPAL ---
const ReportsPage = ({ accounts, currentUser, selectedCurrency, formatCurrency }) => {
    const permissions = currentUser?.permissions || {};
    const isSuperOrAdmin = currentUser?.is_super_user || permissions.manageUsers; 
    
    // NOVO: Estado para armazenar a lista de usuários da empresa
    const [users, setUsers] = useState([]);
    const { apiFetch } = useApi();

    useEffect(() => {
        // Se for admin, busca os usuários para preencher o filtro unificado
        if (isSuperOrAdmin) {
            apiFetch('/reports/users')
                .then(res => { if (Array.isArray(res)) setUsers(res); })
                .catch(err => console.error("Erro ao buscar usuários pro filtro", err));
        }
    }, [isSuperOrAdmin, apiFetch]);

    const allReportOptions = [
        { id: 'livro-caixa', label: 'Livro Caixa', icon: List, permission: 'report_cash_book' },
        { id: 'extrato', label: 'Extrato de Conta', icon: FileText, permission: 'report_ledger' },
        { id: 'a-pagar', label: 'Contas a Pagar', icon: DollarSign, permission: 'report_payables' },
        { id: 'a-receber', label: 'Contas a Receber', icon: DollarSign, permission: 'report_receivables' },
        { id: 'dre', label: 'DRE', icon: PieChart, permission: 'report_dre' },
        { id: 'servicos-cliente', label: 'Serviços por Cliente', icon: Users, permission: 'report_services_client' },
        { id: 'controle-frota', label: 'Controle de Frota', icon: Fuel, permission: 'fleet_fuel_view' },
        // --- NOVO RELATÓRIO DE COMISSÕES INSERIDO NA LISTA ---
        { id: 'pagamento-comissoes', label: 'Pagamento de Comissões', icon: BadgeDollarSign, permission: 'reports_view' }, // Usei a permissão geral de relatórios
    ];

    const reportOptions = useMemo(() => {
        return allReportOptions.filter(opt => {
            if (isSuperOrAdmin) return true;
            return permissions[opt.permission] === true;
        });
    }, [permissions, isSuperOrAdmin]);

    const [activeReport, setActiveReport] = useState('');

    useEffect(() => {
        if (reportOptions.length > 0) {
            if (!activeReport || !reportOptions.find(r => r.id === activeReport)) {
                setActiveReport(reportOptions[0].id);
            }
        } else {
            setActiveReport('');
        }
    }, [reportOptions, activeReport]);

    const renderActiveReport = () => {
        switch (activeReport) {
            // Passamos a lista de usuários e o currentUser para os relatórios que suportam filtro unificado
            case 'livro-caixa': return <CashBookReport selectedCurrency={selectedCurrency} formatCurrency={formatCurrency} users={users} currentUser={currentUser} />;
            case 'extrato': return <AccountStatementReport accounts={accounts} selectedCurrency={selectedCurrency} formatCurrency={formatCurrency} users={users} currentUser={currentUser} />;
            
            case 'a-pagar': return <FinancialStatusReport type="payables" selectedCurrency={selectedCurrency} formatCurrency={formatCurrency} />;
            case 'a-receber': return <FinancialStatusReport type="receivables" selectedCurrency={selectedCurrency} formatCurrency={formatCurrency} />;
            case 'dre': return <DREReport selectedCurrency={selectedCurrency} formatCurrency={formatCurrency} />;
            case 'servicos-cliente': return <ServicesByClientReport selectedCurrency={selectedCurrency} formatCurrency={formatCurrency} />;
            case 'controle-frota': return <FleetFuelReport selectedCurrency={selectedCurrency} formatCurrency={formatCurrency} users={users} currentUser={currentUser} />;
            
            // --- RENDERIZA O NOVO COMPONENTE DE COMISSÕES ---
            case 'pagamento-comissoes': return <CommissionsReport selectedCurrency={selectedCurrency} formatCurrency={formatCurrency} users={users} />;
            
            default: return <p className="text-gray-500">Selecione um relatório acima.</p>;
        }
    };

    if (reportOptions.length === 0) {
        return (
            <div className="p-8 text-center text-gray-500">
                <AlertTriangle className="mx-auto mb-2" size={48} />
                <p>Você não tem permissão para visualizar nenhum relatório.</p>
            </div>
        );
    }

    return (
        <>
            <style type="text/css" media="print">
                {`
                    body * { visibility: hidden; }
                    #print-report-section, #print-report-section * { visibility: visible; }
                    #print-report-section { position: absolute; left: 0; top: 0; width: 100%; margin: 0; padding: 0; }
                    body { background-color: white !important; }
                    
                    /* Melhora as cores de fundo na impressão (Chrome/Edge/Safari exigem isso) */
                    * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
                `}
            </style>

            <div className="print:hidden">
                <h1 className="text-3xl font-black text-gray-800 tracking-tight">Relatórios Gerenciais</h1>
                <p className="mb-6 text-gray-500 font-medium">Unidade de Negócios: <span className="font-bold uppercase text-blue-600">Energia</span></p>
            </div>

            <div id="print-report-section" className="p-6 bg-white rounded-xl shadow-sm border border-gray-200 min-h-[500px] print:shadow-none print:border-none print:p-0">
                
                <div className="hidden print:block text-center border-b-4 border-gray-800 pb-4 mb-8">
                    <h1 className="text-3xl font-black uppercase tracking-widest text-gray-900">Relatório Financeiro Corporativo</h1>
                    <p className="text-lg text-gray-600 mt-2 font-bold">Unidade de Negócios: ENERGIA</p>
                </div>

                <div className="flex border-b border-gray-200 mb-6 overflow-x-auto pb-0 print:hidden scrollbar-hide">
                    {reportOptions.map(opt => (
                        <button 
                            key={opt.id}
                            onClick={() => setActiveReport(opt.id)}
                            className={`flex items-center gap-2 px-5 py-3 text-sm font-bold border-b-2 transition-all whitespace-nowrap ${
                                activeReport === opt.id 
                                ? 'border-blue-600 text-blue-700 bg-blue-50/50' 
                                : 'border-transparent text-gray-500 hover:text-gray-800 hover:bg-gray-50 hover:border-gray-300'
                            }`}
                        >
                            <opt.icon size={18} className={activeReport === opt.id ? 'text-blue-600' : 'text-gray-400'} /> 
                            {opt.label}
                        </button>
                    ))}
                </div>

                <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
                    {renderActiveReport()}
                </div>

                <div className="hidden print:block mt-16 text-center border-t border-gray-300 pt-4 text-xs font-bold text-gray-400 uppercase tracking-widest">
                    Documento gerado pelo sistema integrado • {new Date().toLocaleString('pt-BR')}
                </div>
            </div>
        </>
    );
};

export default ReportsPage;